import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import numeral from "numeral";
import Content from "@/components/specific/Content";
import { useDocumentTitle } from "@/utils/hooks/useDocumentTitle";
import global from "@/utils/helpers/global";
import { getTagihanPelanggan, kirimStrukEmail } from "@/utils/apis/public";
import {
  buildStrukTagihanHtml,
  buildStrukTagihanPdf,
} from "./strukTagihanTemplate";
import "./infopelanggan.css";
import InfoBackground from "./InfoBackground";

function HistoryTagihan() {
  const { search } = useLocation();
  const qparams = new URLSearchParams(search);

  var history = qparams.get("history") ?? null;
  if (history === "null") history = null;

  useDocumentTitle("History Tagihan");

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [pelanggan, setPelanggan] = useState(null);
  const [dataTagihan, setDataTagihan] = useState([]);
  const [isFotoMeterOpen, setIsFotoMeterOpen] = useState(false);
  const [fotoMeterPopupUrl, setFotoMeterPopupUrl] = useState(null);
  const [fotoMeterPopupStatus, setFotoMeterPopupStatus] = useState("idle");

  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);
  const [emailPopupItem, setEmailPopupItem] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailSendStatus, setEmailSendStatus] = useState("idle"); // idle | loading | success | error
  const [emailSendMessage, setEmailSendMessage] = useState("");

  const [payload, setPayload] = useState({
    kode_pelanggan: history || "",
  });

  const getData = async () => {
    if (!payload.kode_pelanggan) return;

    try {
      setIsLoading(true);
      setPelanggan(null);
      setIsError(false);
      setErrorMessage(null);

      const response = await getTagihanPelanggan(payload.kode_pelanggan);

      if (
        response?.success &&
        response?.data?.callback &&
        response?.data?.callback.length > 0
      ) {
        const callbackData = response.data.callback;
        const firstItem = callbackData[0];

        // Map pelanggan data
        const pelangganData = {
          kode_pelanggan: firstItem.kode_pelanggan,
          nama: firstItem.nama_pelanggan,
          alamat: firstItem.alamat || "-",
          golongan: firstItem.nama_golongan,
        };

        // Map tagihan data
        const tagihanData = callbackData.map((item) => ({
          id: item.id,
          periode_tahun: item.periode_tahun,
          periode_bulan: item.periode_bulan,
          m3_awal: item.m3_awal,
          m3_akhir: item.m3_akhir,
          m3_pemakaian: item.m3_pemakaian,
          total_tagihan:
            parseFloat(item.tagihan_air || 0) +
            parseFloat(item.biaya_admin || 0) +
            parseFloat(item.dana_meter || 0) +
            parseFloat(item.denda || 0),
          tagihan_air: parseFloat(item.tagihan_air || 0),
          biaya_admin: parseFloat(item.biaya_admin || 0),
          dana_meter: parseFloat(item.dana_meter || 0),
          denda: parseFloat(item.denda || 0),
          status: item.status_bayar === "S1" ? "BELUM LUNAS" : "LUNAS",
          ...item,
        }));

        // Sort by year descending, then month descending
        const sortedTagihan = tagihanData.sort((a, b) => {
          const byYear = (b.periode_tahun || 0) - (a.periode_tahun || 0);
          if (byYear !== 0) return byYear;
          return (b.periode_bulan || 0) - (a.periode_bulan || 0);
        });

        setPelanggan(pelangganData);
        setDataTagihan(sortedTagihan);
      } else {
        setIsError(true);
        setErrorMessage(
          response?.data?.message || "Data pelanggan tidak ditemukan",
        );
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setIsError(true);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Terjadi kesalahan saat mengambil data",
      );
    }
  };

  useEffect(() => {
    if (history) {
      setPayload((prev) => ({ ...prev, kode_pelanggan: history }));
    }
  }, [history]);

  // Effect to trigger search when payload.kode_pelanggan is set from history on mount
  useEffect(() => {
    if (history && payload.kode_pelanggan === history) {
      getData();
    }
  }, [payload.kode_pelanggan, history]);

  const getFotoMeterUrl = (item) => {
    const pelangganKode =
      pelanggan?.kode_pelanggan ||
      item?.kode_pelanggan ||
      payload?.kode_pelanggan;
    const tahun = item?.periode_tahun;
    const bulan = item?.periode_bulan;
    if (!pelangganKode || !tahun || !bulan) return null;
    const bulanPadded = String(bulan).padStart(2, "0");
    return `https://www.pdamcilegon.co.id/upload/fotometer/${encodeURIComponent(
      tahun,
    )}/${encodeURIComponent(bulanPadded)}/${encodeURIComponent(pelangganKode)}.jpg`;
  };

  const handleViewFotoMeter = (item) => {
    const url = getFotoMeterUrl(item);
    setIsFotoMeterOpen(true);
    if (!url) {
      setFotoMeterPopupUrl(null);
      setFotoMeterPopupStatus("unavailable");
      return;
    }
    setFotoMeterPopupUrl(url);
    setFotoMeterPopupStatus("loading");
  };

  const handleCloseFotoMeter = () => {
    setIsFotoMeterOpen(false);
    setFotoMeterPopupUrl(null);
    setFotoMeterPopupStatus("idle");
  };

  const handlePrintStruk = (item) => {
    if (item?.status !== "LUNAS") return;

    const pelangganNama = pelanggan?.nama || item?.nama_pelanggan || "-";
    const pelangganKode =
      pelanggan?.kode_pelanggan ||
      item?.kode_pelanggan ||
      payload?.kode_pelanggan ||
      "-";
    const html = buildStrukTagihanHtml({
      pelangganKode,
      pelangganNama,
      item,
      printedAt: new Date(),
    });

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");
    if (!printWindow) {
      URL.revokeObjectURL(url);
      return;
    }
    try {
      printWindow.opener = null;
    } catch (e) {}
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleOpenEmailPopup = (item) => {
    if (item?.status !== "LUNAS") return;
    setEmailPopupItem(item);
    setEmailInput("");
    setEmailSendStatus("idle");
    setEmailSendMessage("");
    setIsEmailPopupOpen(true);
  };

  const handleCloseEmailPopup = () => {
    setIsEmailPopupOpen(false);
    setEmailPopupItem(null);
    setEmailInput("");
    setEmailSendStatus("idle");
    setEmailSendMessage("");
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    if (!emailPopupItem || !emailInput) return;

    const id_tagihan = String(emailPopupItem.id ?? "");
    const kode_pelanggan =
      pelanggan?.kode_pelanggan ||
      emailPopupItem?.kode_pelanggan ||
      payload?.kode_pelanggan ||
      "";

    try {
      setEmailSendStatus("loading");
      setEmailSendMessage("");
      const response = await kirimStrukEmail({
        id_tagihan,
        kode_pelanggan,
        email: emailInput,
      });
      if (response?.success) {
        setEmailSendStatus("success");
        setEmailSendMessage(
          response?.data?.message || "Struk berhasil dikirim ke email.",
        );
      } else {
        setEmailSendStatus("error");
        setEmailSendMessage(response?.data?.message || "Gagal mengirim struk.");
      }
    } catch (err) {
      setEmailSendStatus("error");
      setEmailSendMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Terjadi kesalahan saat mengirim struk.",
      );
    }
  };

  const handleDownloadStruk = async (item) => {
    if (item?.status !== "LUNAS") return;

    const pelangganNama = pelanggan?.nama || item?.nama_pelanggan || "-";
    const pelangganKode =
      pelanggan?.kode_pelanggan ||
      item?.kode_pelanggan ||
      payload?.kode_pelanggan ||
      "-";
    const doc = await buildStrukTagihanPdf({
      pelangganKode,
      pelangganNama,
      item,
      printedAt: new Date(),
    });

    const periode = `${item?.periode_tahun || ""}-${String(item?.periode_bulan || "").padStart(2, "0")}`;
    const filename = `struk-tagihan-${pelangganKode}-${periode}.pdf`;
    doc.save(filename);
  };

  return (
    <>
      <InfoBackground />
      <Content showContentHeader={false}>
        <div className="ip">
          <div className="section">
            <div className="wrap">
              {/* Header */}
              <div className="head-row mb-6" style={{ marginBottom: 24 }}>
                <div>
                  <span className="eyebrow">Riwayat Pembayaran</span>
                  <h1 style={{ margin: "16px 0 6px" }}>
                    Riwayat <span className="hl">Tagihan.</span>
                  </h1>
                  <p className="sub">
                    Informasi lengkap pembayaran & penggunaan air. Struk dapat
                    dicetak, diunduh, atau dikirim ke email untuk tagihan yang
                    telah lunas.
                  </p>
                </div>
                <Link
                  to={`/info-tagihan?history=${payload.kode_pelanggan}`}
                  className="btn-ip btn-outline btn-sm"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M11 18l-6-6 6-6" />
                  </svg>
                  Kembali
                </Link>
              </div>

              {isError && errorMessage && (
                <div className="alert-ip mt">{errorMessage}</div>
              )}

              {/* Data Pelanggan */}
              {/* <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden mb-6">
                                  <div className="bg-[#095393] px-6 py-4">
                                      <span className="text-lg font-semibold text-white flex items-center">
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                          </svg>
                                          Data Pelanggan
                                      </span>
                                  </div>
                                  <div className="p-6">
                                      <div className="space-y-3">
                                          <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-200">
                                              <div className="sm:w-2/5 mb-1 sm:mb-0">
                                                  <span className="text-gray-600 font-medium text-sm">Nomor Sambung</span>
                                              </div>
                                              <div className="sm:w-3/5">
                                                  <span className="text-gray-900 font-semibold">{pelanggan.kode_pelanggan}</span>
                                              </div>
                                          </div>
                                          <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-200">
                                              <div className="sm:w-2/5 mb-1 sm:mb-0">
                                                  <span className="text-gray-600 font-medium text-sm">Nama Pelanggan</span>
                                              </div>
                                              <div className="sm:w-3/5">
                                                  <span className="text-gray-900">{pelanggan.nama}</span>
                                              </div>
                                          </div>
                                          <div className="flex flex-col sm:flex-row sm:items-center py-3">
                                              <div className="sm:w-2/5 mb-1 sm:mb-0">
                                                  <span className="text-gray-600 font-medium text-sm">Golongan</span>
                                              </div>
                                              <div className="sm:w-3/5">
                                                  <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                                      {pelanggan.golongan}
                                                  </span>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              </div> */}

              {dataTagihan.length === 0 ? (
                <div className="state-card-ip mt-lg">Tidak ada tagihan</div>
              ) : (
                <div className="mt-lg">
                  {dataTagihan.map((item) => (
                    <div className="inv" key={item.id}>
                      <div className="inv-top">
                        <div className="per">
                          <span className="m">
                            {global.convert_month(item.periode_bulan)}
                          </span>
                          <span className="y">{item.periode_tahun}</span>
                        </div>
                        {item.status === "LUNAS" ? (
                          <span className="pill pill-green">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            LUNAS
                          </span>
                        ) : (
                          <span className="pill pill-red">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                            BELUM LUNAS
                          </span>
                        )}
                      </div>
                      <div className="inv-body">
                        <div className="meter">
                          <div className="mc">
                            <div className="k">Awal</div>
                            <div className="v">
                              {numeral(item.m3_awal).format("0,0")}
                            </div>
                          </div>
                          <div className="mc">
                            <div className="k">Akhir</div>
                            <div className="v">
                              {numeral(item.m3_akhir).format("0,0")}
                            </div>
                          </div>
                          <div className="mc hl">
                            <div className="k">Pakai</div>
                            <div className="v">
                              {numeral(item.m3_pemakaian).format("0,0")}{" "}
                              <small>m³</small>
                            </div>
                          </div>
                        </div>
                        <div className="break">
                          <div className="r">
                            <span>Tagihan Air</span>
                            <span>
                              Rp {numeral(item.tagihan_air).format("0,0")}
                            </span>
                          </div>
                          <div className="r">
                            <span>Admin</span>
                            <span>
                              Rp {numeral(item.biaya_admin).format("0,0")}
                            </span>
                          </div>
                          <div className="r">
                            <span>Dana Meter</span>
                            <span>
                              Rp {numeral(item.dana_meter).format("0,0")}
                            </span>
                          </div>
                          <div className="r">
                            <span>Denda</span>
                            <span>Rp {numeral(item.denda).format("0,0")}</span>
                          </div>
                          <div className="r total">
                            <span>Total</span>
                            <b>
                              Rp{" "}
                              {numeral(
                                parseFloat(item.tagihan_air || 0) +
                                  parseFloat(item.biaya_admin || 0) +
                                  parseFloat(item.dana_meter || 0) +
                                  parseFloat(item.denda || 0),
                              ).format("0,0")}
                            </b>
                          </div>
                        </div>
                      </div>
                      <div className="inv-foot">
                        <button
                          type="button"
                          className="mini g"
                          onClick={() => handleViewFotoMeter(item)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                            <rect x="3" y="5" width="18" height="14" rx="2" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          Foto Meter
                        </button>
                        <button
                          type="button"
                          className={`mini${item.status !== "LUNAS" ? " disabled" : ""}`}
                          onClick={() => handlePrintStruk(item)}
                          disabled={item.status !== "LUNAS"}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                            <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z" />
                          </svg>
                          Print Struk
                        </button>
                        <button
                          type="button"
                          className={`mini${item.status !== "LUNAS" ? " disabled" : ""}`}
                          onClick={() => handleDownloadStruk(item)}
                          disabled={item.status !== "LUNAS"}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                            <path d="M12 15V3M7 10l5 5 5-5M4 21h16" />
                          </svg>
                          Download PDF
                        </button>
                        <button
                          type="button"
                          className={`mini${item.status !== "LUNAS" ? " disabled" : ""}`}
                          onClick={() => handleOpenEmailPopup(item)}
                          disabled={item.status !== "LUNAS"}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                            <rect x="3" y="5" width="18" height="14" rx="2" />
                            <path d="M3 7l9 6 9-6" />
                          </svg>
                          Kirim Email
                        </button>
                      </div>
                    </div>
                  ))}
                  <p style={{ fontSize: 12.5, color: "var(--ip-ink-3)", textAlign: "center", marginTop: 12 }}>
                    Cetak, unduh, dan kirim email hanya tersedia untuk tagihan
                    berstatus <b style={{ color: "var(--ip-green)" }}>LUNAS</b>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Content>

      {isEmailPopupOpen && (
        <div className="ip">
          <div className="ipmodal" onClick={handleCloseEmailPopup}>
            <div className="box" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="x" onClick={handleCloseEmailPopup}>
                ✕
              </button>
              <div className="card-head" style={{ marginBottom: 6 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
                Kirim Struk ke Email
              </div>
              {emailPopupItem && (
                <p style={{ fontSize: 13.5, color: "var(--ip-ink-3)", marginBottom: 18 }}>
                  Tagihan periode{" "}
                  <b style={{ color: "var(--ip-ink)" }}>
                    {global.convert_month(emailPopupItem.periode_bulan)}{" "}
                    {emailPopupItem.periode_tahun}
                  </b>
                </p>
              )}

              {emailSendStatus === "success" ? (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-green-500"
                    style={{ margin: "0 auto 12px" }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p style={{ color: "var(--ip-green)", fontWeight: 600, marginBottom: 12 }}>
                    {emailSendMessage}
                  </p>
                  <button
                    type="button"
                    onClick={handleCloseEmailPopup}
                    className="btn-ip btn-primary btn-sm"
                  >
                    Tutup
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitEmail}>
                  <label className="fl">Alamat Email</label>
                  <input
                    type="email"
                    required
                    placeholder="contoh@email.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    disabled={emailSendStatus === "loading"}
                  />

                  {emailSendStatus === "error" && (
                    <div className="alert-ip mt">{emailSendMessage}</div>
                  )}

                  <div className="m-actions">
                    <button
                      type="button"
                      onClick={handleCloseEmailPopup}
                      className="btn-ip btn-outline"
                      disabled={emailSendStatus === "loading"}
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      className="btn-ip btn-primary grow"
                      disabled={emailSendStatus === "loading"}
                    >
                      {emailSendStatus === "loading" ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                      )}
                      {emailSendStatus === "loading" ? "Mengirim..." : "Kirim Struk"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {isFotoMeterOpen && (
        <div className="ip">
          <div className="ipmodal" onClick={handleCloseFotoMeter}>
            <div className="box" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="x" onClick={handleCloseFotoMeter}>
                ✕
              </button>
              <div className="card-head" style={{ marginBottom: 6 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Foto Meter
              </div>

              {(fotoMeterPopupStatus === "unavailable" ||
                fotoMeterPopupStatus === "error") && (
                <div className="foto-big">
                  <p style={{ fontSize: 13, textAlign: "center", padding: "0 16px", color: "var(--ip-ink-3)" }}>
                    Foto Meter tidak tersedia saat ini.
                    <br />
                    Untuk informasi lebih lanjut, silakan menghubungi customer
                    service atau datang langsung ke kantor kami.
                  </p>
                </div>
              )}

              {fotoMeterPopupStatus !== "unavailable" &&
                fotoMeterPopupStatus !== "error" && (
                  <div>
                    {fotoMeterPopupStatus === "loading" && (
                      <p style={{ textAlign: "center", fontSize: 13, color: "var(--ip-ink-3)", marginBottom: 8 }}>
                        Memuat foto...
                      </p>
                    )}
                    {fotoMeterPopupUrl && (
                      <div className="foto-big">
                        <img
                          src={fotoMeterPopupUrl}
                          alt="Foto Meter"
                          onLoad={() => setFotoMeterPopupStatus("loaded")}
                          onError={() => setFotoMeterPopupStatus("error")}
                        />
                      </div>
                    )}
                  </div>
                )}
              <p style={{ fontSize: 12.5, color: "var(--ip-ink-3)", marginTop: 12, textAlign: "center" }}>
                Foto pencatatan meter — periode terpilih.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HistoryTagihan;
